import { loanInfoConfigFields } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.config';
import _ from 'lodash';

import { FieldType } from 'constants/formGenerator';
import { EscrowRequirementType, LoanType } from 'constants/loanApplication';
import { getConditionalObject } from 'utils/data';

import {
  ESCROW_LABELS,
  ESCROW_TAXES_PATH,
  INSURANCE_ESCROWED_FIELD_NAME,
} from './LoanEditorCustomFormDrawer.constants';

const buildInsuranceEscrowedPath = insuranceId =>
  loanInfoConfigFields.isInsuranceEscrowed.nameInModel.replace('[0]', `[${insuranceId}]`);

export const buildEscrowConfigInputs = ({
  escrowSettings,
  requireEscrow,
  mortgageType,
  escrowRequirement,
  impoundTypes,
  isEscrowed,
  onMasterChange,
}) => {
  const { floodInsuranceImpoundId = null, insuranceImpoundsEscrowed } =
    escrowSettings?.insuranceEscrowSettings ?? {};
  const insurances = Array.isArray(insuranceImpoundsEscrowed) ? insuranceImpoundsEscrowed : [];

  return {
    isEscrowed: !!isEscrowed,
    isEscrowLocked: !!requireEscrow || mortgageType === LoanType.FHA,
    isMasterLockedByFlood:
      !!floodInsuranceImpoundId && escrowRequirement === EscrowRequirementType.OPTIONAL,
    floodInsuranceImpoundId,
    insuranceIds: _.map(insurances, 'applicationInsuranceId'),
    insuranceLabelsById: insurances.reduce(
      (acc, { applicationInsuranceId, impoundTypeId }) => ({
        ...acc,
        [applicationInsuranceId]:
          applicationInsuranceId === floodInsuranceImpoundId
            ? ESCROW_LABELS.FLOOD
            : impoundTypes?.[impoundTypeId]?.name,
      }),
      {},
    ),
    onMasterChange,
  };
};

const withSwitchProps = ({ field, label, disabled, customOnChange }) => ({
  ...field,
  type: FieldType.SWITCH,
  inputProps: {
    ...field.inputProps,
    label,
    disabled,
    ...getConditionalObject(!!customOnChange, { customOnChange }),
  },
});

const parseInsuranceId = nameInModel =>
  nameInModel.match(/insuranceEscrowSettings\[(.+)\]\.isEscrowed$/)?.[1] ?? null;

const decorateEscrowField = ({ field, escrowConfigInputs }) => {
  const {
    isEscrowed,
    isEscrowLocked,
    isMasterLockedByFlood,
    floodInsuranceImpoundId,
    insuranceLabelsById,
    onMasterChange,
  } = escrowConfigInputs;

  switch (field.fieldId) {
    case loanInfoConfigFields.isEscrowed.fieldId:
      return withSwitchProps({
        field,
        label: ESCROW_LABELS.MASTER,
        disabled: isEscrowLocked || isMasterLockedByFlood,
        customOnChange: onMasterChange,
      });

    case loanInfoConfigFields.isTaxesEscrowed.fieldId:
      return withSwitchProps({
        field,
        label: ESCROW_LABELS.TAXES,
        disabled: isEscrowLocked || !isEscrowed,
      });

    case INSURANCE_ESCROWED_FIELD_NAME: {
      const insuranceId = parseInsuranceId(field.nameInModel);

      return withSwitchProps({
        field,
        label: `${insuranceLabelsById[insuranceId] ?? ''}:`,
        disabled: isEscrowLocked || !isEscrowed || insuranceId === floodInsuranceImpoundId,
      });
    }

    default:
      return field;
  }
};

export const decorateEscrowGroups = ({ groups, escrowConfigInputs }) =>
  escrowConfigInputs
    ? groups.map(group => ({
        ...group,
        fields: group.fields.map(field => decorateEscrowField({ field, escrowConfigInputs })),
      }))
    : groups;

export const getEscrowCascadeFieldPaths = escrowConfigInputs => [
  ESCROW_TAXES_PATH,
  ...(escrowConfigInputs?.insuranceIds ?? []).map(buildInsuranceEscrowedPath),
];

export const checkIsEscrowMasterFieldName = fieldName =>
  fieldName === loanInfoConfigFields.isEscrowed.fieldId;

export const buildEscrowMasterChangeHandler =
  ({ insuranceIds, floodInsuranceImpoundId, handleChange, setMultipleInForm }) =>
  event => {
    const isChecked = !!event.target.checked;

    if (!isChecked) {
      setMultipleInForm({
        [ESCROW_TAXES_PATH]: false,
        ...insuranceIds.reduce(
          (acc, insuranceId) => ({ ...acc, [buildInsuranceEscrowedPath(insuranceId)]: false }),
          {},
        ),
      });
    }

    if (isChecked && floodInsuranceImpoundId) {
      setMultipleInForm({ [buildInsuranceEscrowedPath(floodInsuranceImpoundId)]: true });
    }

    return handleChange(event);
  };
